import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Flame, RefreshCw, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export function ZapalnikView({ scene, onUpdate, onNavigate }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [text, setText] = useState(scene.zapalnik || "");
  const [busy, setBusy] = useState(false);

  const hasMap = (scene.emotional_map || []).length > 0;

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/zapalnik`);
      setText(r.data.zapalnik);
      onUpdate?.({ zapalnik: r.data.zapalnik });
      toast.success(t("zap.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("an.fail"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-amber-500">{t("zap.eyebrow")}</div>
          <h2 className="serif text-3xl text-stone-100 mt-1">{t("zap.title")}</h2>
          <p className="text-stone-400 text-sm mt-2">{t("zap.subtitle")}</p>
        </div>
        <button data-testid="zapalnik-generate-btn" onClick={generate} disabled={busy} className="pill-btn">
          {busy ? <><RefreshCw size={14} className="animate-spin"/> {t("zap.generating")}</> : (text ? <><RefreshCw size={14}/> {t("zap.regenerate")}</> : <><Flame size={14}/> {t("zap.generate")}</>)}
        </button>
      </div>

      {!hasMap && !text && (
        <div className="mono text-[11px] text-amber-500/80 mt-3 tracking-wider">{t("zap.need_map")}</div>
      )}

      {!text ? (
        <div className="paper p-10 text-center mt-8">
          <Flame className="mx-auto text-amber-500"/>
          <div className="text-stone-400 text-sm mt-3">{t("zap.empty")}</div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="paper p-8 mt-8 border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-stone-900 to-stone-900"
          data-testid="zapalnik-text"
        >
          <div className="serif text-lg text-stone-100 italic leading-relaxed whitespace-pre-wrap">
            {text}
          </div>
        </motion.div>
      )}

      {onNavigate && (
        <div className="mt-8 pt-6 border-t border-stone-800/60 flex justify-end">
          <button data-testid="nav-to-eight-btn" onClick={() => onNavigate("eight")} className="pill-btn">
            {t("nav.to_eight")} <ArrowRight size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}
